import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError, getTeamAvatarUrl, handleTeamAvatarError } from '../utils/avatars';
import { getViolationRiderName } from '../utils/violations';
import EventTeamEntry from './EventTeamEntry';
import EventTeamRosterModal from './EventTeamRosterModal';
import EventTeamInviteModal from './EventTeamInviteModal';
import TeamMembersModal from './TeamMembersModal';
import './EventTeamsSection.css';

// Иконки действий с командой (stroke-иконки 24×24, как в остальных списках)
const ACTION_ICONS = {
  invite: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </>
  ),
  eventRoster: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <polyline points="17 11 19 13 23 9" />
    </>
  ),
  roster: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  edit: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  ),
  detach: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  )
};

// Команды события (Api::EventTeamsController): команды под событие (в общем
// списке команд их нет) и заявленные основные команды. В карточке — состав на
// событие: за команду в этом событии выступают только они
const EventTeamsSection = ({ event, user }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [inviteTeam, setInviteTeam] = useState(null);
  const [rosterTeam, setRosterTeam] = useState(null);
  // Правка состава на событие
  const [eventRosterTeam, setEventRosterTeam] = useState(null);
  // Подпись иконки: показываем над кнопкой при наведении
  const [tooltip, setTooltip] = useState(null);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await axios.get(`/api/events/${event.id}/teams`);
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('eventTeams.loadError'));
    }
  }, [event.id, t]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams, user?.id]);

  const handleDetach = async (team) => {
    if (!window.confirm(t('eventTeams.confirmDetach').replace('{team}', team.name))) return;

    try {
      await axios.delete(`/api/events/${event.id}/teams/${team.id}`);
      fetchTeams();
    } catch (err) {
      alert(err.response?.data?.error || t('common.error'));
    }
  };

  const showTooltip = (e, text) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, top: rect.top - 8, left: rect.left + rect.width / 2 });
  };

  const iconButton = (kind, label, onClick) => (
    <button
      type="button"
      className={`event-team-icon event-team-icon-${kind}`}
      aria-label={label}
      onClick={() => {
        setTooltip(null);
        onClick();
      }}
      onMouseEnter={(e) => showTooltip(e, label)}
      onMouseLeave={() => setTooltip(null)}
      onFocus={(e) => showTooltip(e, label)}
      onBlur={() => setTooltip(null)}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {ACTION_ICONS[kind]}
      </svg>
    </button>
  );

  if (!data && !error) return null;

  const teams = data?.teams || [];
  const availableTeams = data?.available_teams || [];
  const canAdd = Boolean(user && (data?.can_create || availableTeams.length > 0));

  return (
    <div className="event-teams-section">
      <div className="event-teams-header">
        <h2 className="event-teams-title">{t('eventTeams.title')}</h2>
        {canAdd && (
          <button type="button" className="event-teams-add" onClick={() => setShowForm(true)}>
            {data.can_create ? t('eventTeams.create') : t('eventTeams.attach')}
          </button>
        )}
      </div>

      {data && (
        <div className="event-teams-hint">
          <span>{t('eventTeams.hint')}</span>
          {data.members_accounting && (
            <span>{t('eventTeams.hintAccounting').replace('{count}', data.members_accounting)}</span>
          )}
          {data.max_team_members && (
            <span>{t('eventTeams.hintLimit').replace('{max}', data.max_team_members)}</span>
          )}
          {!data.accepts_teams && <span>{t('eventTeams.closed')}</span>}
        </div>
      )}

      {error && <div className="event-teams-error">{error}</div>}

      {data && teams.length === 0 && (
        <div className="event-teams-empty">{t('eventTeams.empty')}</div>
      )}

      {teams.length > 0 && (
        <div className="event-teams-grid">
          {teams.map(team => (
            <div key={team.id} className="event-team-card">
              <div className="event-team-card-header">
                <img
                  src={getTeamAvatarUrl(team.avatar, team.name)}
                  alt=""
                  className="event-team-avatar"
                  onError={(e) => handleTeamAvatarError(e, team.name)}
                />
                <div className="event-team-heading">
                  <div className="event-team-name">{team.name}</div>
                  <div className="event-team-manager">
                    {t('eventTeams.manager')}: @{team.manager.nickname}
                  </div>
                </div>
                {!team.event_team && (
                  <span className="event-team-kind">{t('eventTeams.mainTeam')}</span>
                )}
                {team.status === 'inactive' && (
                  <span className="event-team-status">{t('eventTeams.inactive')}</span>
                )}
              </div>

              <div className="event-team-count">
                {t('eventTeams.membersCount')}: {team.members_count}
                {data.max_team_members ? ` / ${data.max_team_members}` : ''}
              </div>

              {team.members.length > 0 ? (
                <ul className="event-team-members">
                  {team.members.map(member => (
                    <li key={member.id} className="event-team-member">
                      <img
                        src={getUserAvatarUrl(member.avatar)}
                        alt=""
                        className="event-team-member-avatar"
                        onError={handleUserAvatarError}
                      />
                      <span className="event-team-member-name">{getViolationRiderName(member, language)}</span>
                      {member.role === 'captain' && (
                        <span className="event-team-captain">{t('eventTeams.captain')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="event-team-no-members">{t('eventTeams.noMembers')}</div>
              )}

              {user && (
                <div className="event-team-actions">
                  {team.can_invite && iconButton('invite', t('eventTeams.invite'), () => setInviteTeam(team))}
                  {team.can_edit_roster && iconButton('eventRoster', t('eventTeams.rosterTitle'), () => setEventRosterTeam(team))}
                  {iconButton('roster', t('eventTeams.roster'), () => setRosterTeam(team))}
                  {team.can_manage && iconButton('edit', t('eventTeams.edit'),
                    () => navigate(`/teams/${team.id}/edit?event=${event.id}`))}
                  {team.can_detach && iconButton('detach', t('eventTeams.detach'), () => handleDetach(team))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tooltip && (
        <div className="event-team-tooltip" style={{ top: tooltip.top, left: tooltip.left }}>
          {tooltip.text}
        </div>
      )}

      {showForm && (
        <EventTeamEntry
          event={event}
          data={data}
          onClose={() => setShowForm(false)}
          onDone={() => {
            setShowForm(false);
            fetchTeams();
          }}
        />
      )}

      {eventRosterTeam && (
        <EventTeamRosterModal
          event={event}
          team={eventRosterTeam}
          mode="edit"
          onClose={() => setEventRosterTeam(null)}
          onSaved={() => {
            setEventRosterTeam(null);
            fetchTeams();
          }}
        />
      )}

      {inviteTeam && (
        <EventTeamInviteModal
          event={event}
          team={inviteTeam}
          onClose={() => {
            setInviteTeam(null);
            fetchTeams();
          }}
        />
      )}

      {rosterTeam && (
        <TeamMembersModal
          team={rosterTeam}
          onClose={() => {
            setRosterTeam(null);
            fetchTeams();
          }}
        />
      )}
    </div>
  );
};

export default EventTeamsSection;
